import React, { useState } from 'react'
import { Box, IconButton, useForkRef } from '@mui/material'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import { CalendarIcon, PickerChangeHandlerContext, usePickerContext } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment from 'moment'
import { useTranslate } from 'src/i18n/useMessageSource'
import { formatDateForLocale } from 'src/service/utils/DateFormatUtils'
import { getEndOfWeekMoment, getStartOfWeekUTC } from 'src/service/utils/MomentUtils'
import { getLocaleForDatePicker } from 'src/service/view-model/base/localization/Locales'
import { DatePickerDefault } from 'src/ui-shared/base/form/control/DatePickerDefault'
import { DATE_PICKER_ADORNMENT_BUTTON_STYLE, useTextFieldStyles } from 'src/ui-shared/base/form/control/TextField.style'
import { TextFieldDefault } from 'src/ui-shared/base/form/control/TextFieldDefault'
import { useUserLocale, useUserRegionLocale } from 'src/user/UserContext'

interface WeekDayPickerProps {
  onChange: (date: Date | null) => void
  startDate?: Date
  delay?: number
}

export const WeekPicker: React.FC<WeekDayPickerProps> = ({ onChange, delay = 300, startDate }) => {
  const translate = useTranslate()
  const locale = useUserLocale()
  const regionLocale = useUserRegionLocale()

  const [startOfWeekDate, setStartOfWeekDate] = useState<Date>(
    startDate ? getStartOfWeekUTC(startDate) : getStartOfWeekUTC(new Date()),
  )
  const [timeoutState, setTimeoutState] = useState<any>(null)

  const decrementWeek = () => {
    const newWeek = moment(startOfWeekDate).subtract(7, 'days').toDate()
    setStartOfWeekDate(newWeek)

    debounceFunction(newWeek)
  }

  const incrementWeek = () => {
    const newWeek = moment(startOfWeekDate).add(7, 'days').toDate()
    setStartOfWeekDate(newWeek)

    debounceFunction(newWeek)
  }

  const debounceFunction = (newWeek: Date) => {
    if (timeoutState) {
      clearTimeout(timeoutState)
    }

    setTimeoutState(
      setTimeout(() => {
        onChange(newWeek)
      }, delay),
    )
  }

  const changeCalendarDay = (date: Date | null, context: PickerChangeHandlerContext<any>) => {
    if (!date || context.validationError) {
      return
    }

    const startOfWeek = moment(date).startOf('isoWeek').toDate()
    setStartOfWeekDate(startOfWeek)
    onChange(startOfWeek)
  }

  const startDateMoment = moment(startOfWeekDate)
  const startDateFormatted = formatDateForLocale(startDateMoment.toDate(), regionLocale)
  const endDateFormatted = formatDateForLocale(getEndOfWeekMoment(startDateMoment).toDate(), regionLocale)

  return (
    <Box>
      <Box
        sx={{
          flexDirection: 'row',
          display: 'flex',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton onClick={decrementWeek}>
            <ArrowBackIos htmlColor="rgba(0, 0, 0, 0.7)" />
          </IconButton>
        </Box>
        <Box>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={getLocaleForDatePicker(locale, regionLocale)}
          >
            <DatePickerDefault
              locale={regionLocale}
              label={translate('selectWeek')}
              views={['day']}
              format="dd.MM.yyyy"
              value={startOfWeekDate}
              onChange={changeCalendarDay}
              slots={{
                field: (props) => {
                  return (
                    <WeekPickerField
                      {...props}
                      startDateFormatted={startDateFormatted}
                      endDateFormatted={endDateFormatted}
                    />
                  )
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton onClick={incrementWeek}>
            <ArrowForwardIos htmlColor="rgba(0, 0, 0, 0.7)" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  )
}

interface CustomWeekPickerFieldProps {
  startDateFormatted: string
  endDateFormatted: string
}

const WeekPickerField = (props: CustomWeekPickerFieldProps) => {
  const { classes: textFieldClasses } = useTextFieldStyles()
  const translate = useTranslate()
  const pickerContext = usePickerContext()

  // Fork the picker context refs so the dropdown snaps exactly to our custom TextField box
  const handleRef = useForkRef(pickerContext.triggerRef, pickerContext.rootRef)

  return (
    <div ref={handleRef}>
      <TextFieldDefault
        {...props}
        className={textFieldClasses.TextFieldSmall}
        label={translate('selectWeek')}
        slotProps={{
          input: {
            endAdornment: (
              <IconButton
                sx={DATE_PICKER_ADORNMENT_BUTTON_STYLE}
                onClick={() => {
                  pickerContext.setOpen((prev) => !prev)
                }}
              >
                <CalendarIcon />
              </IconButton>
            ),
          },
        }}
        onKeyDown={(e) => e.preventDefault()}
        value={`${props.startDateFormatted} - ${props.endDateFormatted}`}
      />
    </div>
  )
}
